/**
 * Emit hosts for the `task` analytics group — 6 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { calculateTimeDeltaSeconds } from '@/lib/utils';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentObjectName, TaskEventType } from '@/models/analytics/segment-names';
import { type TaskEdit, type TrackingProps } from '@/models/analyticsScribeModel';
import { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import { type ClinicalTask } from '@heidi/tasks/contracts';

export function trackClinicalTaskPanelToggle(input: {
  opened: boolean;
  from: 'session' | 'x';
}) {
  let action: TaskEventType;
  if (input.from === 'session') {
    if (input.opened) {
      action = TaskEventType.TaskPanelToggleOpenedFromSession;
    } else {
      action = TaskEventType.TaskPanelToggleClosedFromSession;
    }
  } else {
    action = TaskEventType.TaskPanelToggleClosedViaX;
  }

  trackLegacyEvent({
    name: SegmentObjectName.Task,
    props: {
      action: action,
    },
  });

  // V2 — Amplitude migration
  track({
    event_name: input.opened
      ? TrackingEventName.PanelOpened
      : TrackingEventName.PanelClosed,
    props: {
      panel_type: 'task_panel',
      trigger: 'manual',
    },
  });
}

export function trackTaskEvent(input: {
  taskEventType: TaskEventType;
  sessionId?: string;
  task: ClinicalTask;
}) {
  // XXX: this is a hack that depends on the assumption that tasks
  // XXX: with source_quote are created by the model...
  const taskSource = input.task.source_quote ? 'model' : 'user';

  let props: TrackingProps = {
    taskId: input.task.id,
    action: input.taskEventType,
    currentSessionId: input.sessionId || (input.task.session_id ?? undefined),
    taskCategory: input.task.category ?? undefined,
    taskSource: taskSource,
  };

  if (input.taskEventType === TaskEventType.TaskCompleted) {
    // calculate time to completion and amend relevant meta
    const delta = calculateTimeDeltaSeconds(
      input.task.created_at ?? '',
      input.task.updated_at ?? ''
    );
    props = { ...props, taskTimeToCompletion: delta };
  }

  // if the task is suggested and completed, we track it seperately
  if (
    input.taskEventType == TaskEventType.TaskSuggested &&
    input.task.status == ClinicalTaskStatus.done
  ) {
    props.action = TaskEventType.TaskSuggestedCompleted;
  }

  trackLegacyEvent({
    name: SegmentObjectName.Task,
    props: props,
  });

  // V2 — Amplitude migration
  const v2SessionId = input.sessionId || (input.task.session_id ?? undefined);
  const v2TaskId = input.task.id;
  const v2TaskStatus = input.task.status;
  const v2AssigneeId = input.task.assignee?.id ?? input.task.assignee_id ?? undefined;
  const v2AssigneeType = input.task.assignee?.type ?? undefined;

  if (
    input.taskEventType === TaskEventType.TaskSuggested &&
    input.task.status === ClinicalTaskStatus.done
  ) {
    // suggested + already completed = suggested_completed
    track({
      event_name: TrackingEventName.TaskCompleted,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        completion_action: 'suggested_completed',
        task_category: input.task.category ?? undefined,
        task_source: taskSource,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (input.taskEventType === TaskEventType.TaskSuggested) {
    track({
      event_name: TrackingEventName.TaskCreated,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        creation_action: 'suggested',
        task_category: input.task.category ?? undefined,
        task_source: taskSource,
        task_status: v2TaskStatus,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (input.taskEventType === TaskEventType.TaskCreatedInSession) {
    track({
      event_name: TrackingEventName.TaskCreated,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        creation_action: 'in_session',
        task_category: input.task.category ?? undefined,
        task_source: taskSource,
        task_status: v2TaskStatus,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (input.taskEventType === TaskEventType.TaskCreatedManual) {
    track({
      event_name: TrackingEventName.TaskCreated,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        creation_action: 'manual',
        task_category: input.task.category ?? undefined,
        task_source: taskSource,
        task_status: v2TaskStatus,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (input.taskEventType === TaskEventType.TaskCompleted) {
    const delta = calculateTimeDeltaSeconds(
      input.task.created_at ?? '',
      input.task.updated_at ?? ''
    );
    track({
      event_name: TrackingEventName.TaskCompleted,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        completion_action: 'completed',
        task_category: input.task.category ?? undefined,
        task_source: taskSource,
        task_time_to_completion: delta,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (input.taskEventType === TaskEventType.TaskDeleted) {
    track({
      event_name: TrackingEventName.TaskDeleted,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        task_category: input.task.category ?? undefined,
        task_source: taskSource,
        task_status: v2TaskStatus,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (input.taskEventType === TaskEventType.TaskEdited) {
    track({
      event_name: TrackingEventName.TaskUpdated,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        update_action: 'edited',
        task_category: input.task.category ?? undefined,
        task_source: taskSource,
        task_status: v2TaskStatus,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (input.taskEventType === TaskEventType.TaskRestored) {
    track({
      event_name: TrackingEventName.TaskUpdated,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        update_action: 'restored',
        task_category: input.task.category ?? undefined,
        task_source: taskSource,
        task_status: v2TaskStatus,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  }
}

export function trackTaskListViewedEvent(input: {
  session_id?: string;
  filter_status?: string;
  filter_assignee_id?: string;
  sort_by?: string;
}) {
  track({
    event_name: TrackingEventName.TaskListViewed,
    props: input,
  });
}

export function trackTaskNotificationClickedEvent(input: {
  task_id?: string;
  session_id?: string;
  notification_type: 'assigned' | 'due_soon' | 'overdue';
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TaskNotificationClicked,
    props: input,
  });
}

export function trackTaskStatefulEvent(input: {
  taskEventType: TaskEventType;
  prevTask: Partial<ClinicalTask>;
  nextTask: Partial<ClinicalTask>;
}) {
  let taskEventType = input.taskEventType;
  const edits: TaskEdit[] = [];

  const taskSource = input.nextTask.source_quote ? 'model' : 'user';
  let props: Partial<TrackingProps> = {
    taskId: input.prevTask.id || undefined,
    taskCategory: input.nextTask.category || undefined,
    currentSessionId: input.nextTask.session_id || undefined,
    taskSource: taskSource,
  };

  // determine the task edit types by comparison of fields
  if (input.nextTask.category != input.prevTask.category) {
    edits.push({
      field: 'category',
      previous: input.prevTask.category || '',
      next: input.nextTask.category || '',
    });
  }
  if (input.nextTask.title != input.prevTask.title) {
    edits.push({
      field: 'title',
      changed: true,
    });
  }
  if (input.nextTask.status != input.prevTask.status) {
    // if we add more cases in the future, this will have to be amended
    if (
      input.nextTask.status == ClinicalTaskStatus.done &&
      input.prevTask.status == ClinicalTaskStatus.todo
    ) {
      taskEventType = TaskEventType.TaskCompleted;
      const delta = calculateTimeDeltaSeconds(
        input.nextTask.created_at ?? '',
        input.nextTask.updated_at ?? ''
      );
      props = { ...props, taskTimeToCompletion: delta };
    } else if (
      input.nextTask.status == ClinicalTaskStatus.todo &&
      input.prevTask.status == ClinicalTaskStatus.done
    ) {
      taskEventType = TaskEventType.TaskRestored;
    }
    edits.push({
      field: 'status',
      previous: input.prevTask.status || '',
      next: input.nextTask.status || '',
    });
  }
  trackLegacyEvent({
    name: SegmentObjectName.Task,
    props: {
      ...props,
      action: taskEventType,
      taskEdits: edits,
    },
  });

  // V2 — Amplitude migration
  const v2SessionId = input.nextTask.session_id || undefined;
  const v2TaskId = input.prevTask.id || undefined;
  const v2TaskStatus = input.nextTask.status;
  const v2AssigneeId =
    input.nextTask.assignee?.id ?? input.nextTask.assignee_id ?? undefined;
  const v2AssigneeType = input.nextTask.assignee?.type ?? undefined;

  if (taskEventType === TaskEventType.TaskCompleted) {
    const delta = calculateTimeDeltaSeconds(
      input.nextTask.created_at ?? '',
      input.nextTask.updated_at ?? ''
    );
    track({
      event_name: TrackingEventName.TaskCompleted,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        completion_action: 'completed',
        task_category: input.nextTask.category || undefined,
        task_source: taskSource,
        task_time_to_completion: delta,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (taskEventType === TaskEventType.TaskRestored) {
    track({
      event_name: TrackingEventName.TaskUpdated,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        update_action: 'restored',
        task_category: input.nextTask.category || undefined,
        task_source: taskSource,
        task_edits: JSON.stringify(edits),
        task_status: v2TaskStatus,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  } else if (taskEventType === TaskEventType.TaskEdited) {
    track({
      event_name: TrackingEventName.TaskUpdated,
      props: {
        session_id: v2SessionId,
        task_id: v2TaskId,
        update_action: 'edited',
        task_category: input.nextTask.category || undefined,
        task_source: taskSource,
        task_edits: JSON.stringify(edits),
        task_status: v2TaskStatus,
        assignee_id: v2AssigneeId,
        assignee_type: v2AssigneeType,
      },
    });
  }
}

export function trackTaskViewedEvent(input: {
  task_id?: string;
  session_id?: string;
  view_context: 'task_detail' | 'in_session_card';
}) {
  track({
    event_name: TrackingEventName.TaskViewed,
    props: input,
  });
}
